import React, { useEffect, useMemo, useState } from 'react';
import api from '../../../lib/api';
import { Card } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Badge } from '../../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/select';
import { toast } from 'sonner';
import { Users, UserPlus, Save, Loader2, Search } from 'lucide-react';

export default function ManagerAssignmentsPage() {
  const [trainers, setTrainers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [dirty, setDirty] = useState({}); // trainer_id -> manager_user_id
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ full_name: '', email: '', password: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/training/audit/assignments');
      setTrainers(r.data.trainers || []);
      setManagers(r.data.managers || []);
      setDirty({});
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const valueFor = (t) => (dirty[t.trainer_id] !== undefined ? dirty[t.trainer_id] : (t.manager_user_id || 'none'));
  const setVal = (tid, v) => setDirty((p) => ({ ...p, [tid]: v }));

  const filtered = useMemo(() => trainers.filter((t) => t.trainer_name.toLowerCase().includes(search.trim().toLowerCase())), [trainers, search]);
  const changedCount = Object.keys(dirty).length;

  const save = async () => {
    const assignments = Object.entries(dirty).map(([trainer_id, manager_user_id]) => ({ trainer_id, manager_user_id: manager_user_id === 'none' ? null : manager_user_id }));
    if (!assignments.length) return;
    setSaving(true);
    try {
      await api.post('/training/audit/assignments', { assignments });
      toast.success(`Saved ${assignments.length} assignment(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const createManager = async () => {
    if (!form.full_name || !form.email || !form.password) { toast.error('All fields are required'); return; }
    setCreating(true);
    try {
      await api.post('/training/audit/managers', form);
      toast.success(`Training Manager "${form.full_name}" created`);
      setCreateOpen(false); setForm({ full_name: '', email: '', password: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setCreating(false); }
  };

  const countFor = (mid) => trainers.filter((t) => valueFor(t) === mid).length;

  return (
    <div className="space-y-5" data-testid="assignments-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2"><Users className="w-6 h-6 text-violet-600" />Manager Assignments</h1>
          <p className="text-sm text-slate-500 mt-0.5">Assign each trainer to a Training Manager. That manager audits all of the trainer's batches.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setCreateOpen(true)} className="h-9" data-testid="create-manager-btn"><UserPlus className="w-4 h-4 mr-1.5" />New Training Manager</Button>
          <Button size="sm" onClick={save} disabled={!changedCount || saving} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid="save-assignments-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}Save{changedCount ? ` (${changedCount})` : ''}
          </Button>
        </div>
      </div>

      {/* Manager chips */}
      <div className="flex flex-wrap gap-2">
        {managers.map((m) => (
          <Badge key={m.id} className="bg-violet-50 text-violet-700 border border-violet-200 px-3 py-1 text-[12px]" data-testid={`mgr-chip-${m.id}`}>
            {m.full_name} <span className="ml-1.5 text-violet-400">· {countFor(m.id)} trainers</span> <span className="ml-1.5 text-[10px] text-slate-400">({m.role.replace('_', ' ')})</span>
          </Badge>
        ))}
      </div>

      <Card className="p-4">
        <div className="relative mb-3 max-w-xs">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search trainer…" className="h-9 pl-8" data-testid="trainer-search" />
        </div>
        {loading ? <div className="py-16 text-center text-slate-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead><tr className="text-left text-slate-500 border-b border-slate-200"><th className="py-2 pr-3">Trainer</th><th className="py-2 px-3 w-72">Assigned Training Manager</th></tr></thead>
              <tbody>
                {filtered.map((t) => (
                  <tr key={t.trainer_id} className="border-b border-slate-50" data-testid={`assign-row-${t.trainer_id}`}>
                    <td className="py-2 pr-3 font-medium text-slate-700">{t.trainer_name}</td>
                    <td className="py-2 px-3">
                      <Select value={valueFor(t)} onValueChange={(v) => setVal(t.trainer_id, v)}>
                        <SelectTrigger className="h-9 w-64" data-testid={`assign-select-${t.trainer_id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">— Unassigned —</SelectItem>
                          {managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && <tr><td colSpan={2} className="py-6 text-center text-slate-400">No trainers.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md" data-testid="create-manager-modal">
          <DialogHeader><DialogTitle>New Training Manager</DialogTitle>
            <p className="text-[12px] text-slate-500">Creates an audit-only account. This user can log in with the normal login and lands directly in the Trainer Audit module — no access to the rest of the app.</p>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-[12px]">Full name</Label><Input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} className="mt-1" data-testid="new-mgr-name" /></div>
            <div><Label className="text-[12px]">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} className="mt-1" data-testid="new-mgr-email" /></div>
            <div><Label className="text-[12px]">Password</Label><Input type="text" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} className="mt-1" data-testid="new-mgr-password" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="h-9">Cancel</Button>
            <Button onClick={createManager} disabled={creating} className="h-9 bg-violet-600 hover:bg-violet-700" data-testid="new-mgr-submit">
              {creating && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
